import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column, Alert } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image, CTA } from '../../components';
import ImageBanner from '../../components/common/image-banner';
import Collection from '../../components/template/fno-detail/collection';
import ImageGrid from '../../components/common/image-grid';
import SlickSlider from '../../components/common/slick-slider';
import DynamicComponent from '../../components/common/dynamic-component';
import CardSection from '../../components/common/card-section';
import HeroSimple from '../../components/common/hero-simple';
import { isValidArray } from '../../components/utilities/validations';
import styles from './index.module.scss';

const FNODetail = (props) => {
	const pageData = props.data.contentstackDetailFno;

	return (
		<Layout
			id='template-FNODetail'
			pageType={pageData.seo.page_type}
		>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.special && (
				<Container fluid>
					<Alert
						type='error'
						className='mb-4 text-center'
						hideClose
					>
						<span
							dangerouslySetInnerHTML={{
								__html: pageData.special,
							}}
						/>
					</Alert>
				</Container>
			)}

			{pageData.page_hero?.[0] && <HeroSimple data={pageData.page_hero[0]} />}

			{pageData.modular_blocks?.[0] &&
				pageData.modular_blocks.map((block, index) => {
					if (block.top_content != null) {
						return (
							<div
								className={`mb-10 text-center md:my-16`}
								key={index}
							>
								<Container width={1200}>
									{block.top_content.heading && <h2 className={`mb-6 md:mb-8`}>{block.top_content.heading}</h2>}

									{block.top_content.description && (
										<div
											dangerouslySetInnerHTML={{
												__html: block.top_content.description,
											}}
											className={`md:text-lg`}
										/>
									)}
								</Container>
							</div>
						);
					}

					if (block.callout != null) {
						return (
							<div
								key={index}
								className='my-10 md:my-16'
							>
								<Container width={1200}>
									<Row
										className={styles.fnoFeatures}
										justify='around'
										align='center'
									>
										{block.callout.main_image?.[0] && (
											<Column
												xs={12}
												md='auto'
												className={styles.fnoFeatures_ImageColumn}
											>
												<Image
													data={block.callout.main_image[0]}
													className={styles.fnoFeatures_Image}
												/>
											</Column>
										)}

										<Column
											xs={12}
											sm={9}
											md={6}
										>
											{block.callout.heading && <h3 className='s2-h3__alt'>{block.callout.heading}</h3>}

											{block.callout.callout_text?.[0] && (
												<div
													className={styles.fnoFeatures_List}
													dangerouslySetInnerHTML={{
														__html: block.callout.callout_text,
													}}
												/>
											)}
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.images != null) {
						return (
							<div
								className='my-10 md:my-16'
								key={index}
							>
								{block.images?.grid?.[0] && (
									<Container>
										<ImageGrid
											data={block.images.grid[0]}
											className={styles.fnoImageGrid}
										/>
									</Container>
								)}

								{block.images?.banner?.[0] && <ImageBanner data={block.images.banner[0]} />}
							</div>
						);
					}

					if (block.all_dynamic != null) {
						return (
							<DynamicComponent
								data={block.all_dynamic}
								key={index}
							/>
						);
					}

					if (block.swatches != null) {
						return (
							<div
								className={`my-10 md:my-16`}
								key={index}
							>
								<Container
									width={1200}
									className={`px-0 md:px-8 xl:px-16`}
								>
									<div
										key={index}
										id={block.swatches.anchor || ''}
										className={index > 0 ? 'mt-10 md:mt-16' : null}
									>
										{block.swatches.heading && <h2 className={`mb-6 md:mb-8`}>{block.swatches.heading}</h2>}
										{block.swatches.subheading && <h3 className={styles.fnoCollection_Subheading}>{block.swatches.subheading}</h3>}
										{block.swatches.description && (
											<div
												dangerouslySetInnerHTML={{
													__html: block.swatches.description,
												}}
												className={`${styles.fnoCollection_Description} mb-8 px-5 md:px-0`}
											/>
										)}

										{block.swatches.image_groups?.[0] &&
											block.swatches.image_groups.map((options, index) => {
												if (options.carousel) {
													return (
														<div
															id={options.anchor_text || null}
															key={index}
															className={`py-8 md:py-12`}
														>
															{options.heading && <h3>{options.heading}</h3>}

															{options.description && (
																<div
																	dangerouslySetInnerHTML={{
																		__html: options.description,
																	}}
																/>
															)}

															{isValidArray(options?.items) && (
																<SlickSlider
																	slidesToShow={3}
																	className={styles.fnoCarousel}
																	responsive={[
																		{
																			breakpoint: 1200,
																			settings: {
																				slidesToShow: 3,
																			},
																		},
																		{
																			breakpoint: 1000,
																			settings: {
																				slidesToShow: 2,
																			},
																		},
																		{
																			breakpoint: 500,
																			settings: {
																				slidesToShow: 1,
																			},
																		},
																	]}
																>
																	{isValidArray(options?.items?.[0]?.options_images) &&
																		options.items[0].options_images.map((item, index) => (
																			<div key={index}>
																				<Image data={item} />
																			</div>
																		))}
																</SlickSlider>
															)}
														</div>
													);
												}

												if (!options.carousel) {
													return (
														<div
															key={index}
															id={options.anchor_text}
														>
															{index === 0 ? '' : <hr />}

															{options.items?.[0] && (
																<Collection
																	title={options.heading}
																	content={options.description}
																	data={options.items}
																	isSingle={options.single_image}
																	isFeature={options.feature}
																	id={index}
																/>
															)}
														</div>
													);
												}
											})}

										{block.swatches.ctas?.[0] && (
											<div className={`mt-5 px-5 text-center lg:mt-8`}>
												<CTA data={block.swatches.ctas[0]} />
											</div>
										)}
									</div>
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
							/>
						);
					}
				})}
		</Layout>
	);
};

FNODetail.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default FNODetail;

// eslint-disable-next-line react/prop-types -- fix
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix
		{...data.contentstackDetailFno.seo}
	/>
);

export const query = graphql`
	query ($uid: String!) {
		contentstackDetailFno(id: { eq: $uid }) {
			url
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			special
			page_hero {
				...NewHero
			}
			modular_blocks {
				top_content {
					heading
					description
					cta {
						...CTA
					}
				}
				callout {
					heading
					main_image {
						...Image
						image {
							gatsbyImageData(quality: 90)
						}
					}
					callout_text
				}
				images {
					grid {
						...ImageGrid
					}
					banner {
						...ImageCallout
					}
				}
				all_dynamic {
					heading
					dynamic {
						...Dynamic
					}
				}
				swatches {
					heading
					anchor
					subheading
					description
					image_groups {
						anchor_text
						heading
						description
						single_image
						feature
						carousel
						items {
							options_title
							options_images {
								...Image
								image {
									gatsbyImageData(quality: 90)
								}
							}
						}
					}
					ctas {
						...CTA
					}
				}
				cards {
					...CardSection
				}
			}
			special
			seo {
				title
				description
				noIndex: no_index_page
				page_type
			}
		}
	}
`;
